import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useOutletContext, useSearchParams } from 'react-router'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { invalidateCachedFetch, useAuth } from '@structural-climbing/auth'
import { holdsPlan } from '@structural-climbing/ui'
import { CheckInHero } from '../features/checkin/CheckInHero'
import {
  CodeOverlay,
  childCodePayload,
  memberCodePayload,
  type CodePerson,
} from '../features/checkin/CodeOverlay'
import { useMySubscription } from '../features/membership/useMySubscription'
import { useMemberCheckInStatus } from '../features/checkin/useMemberCheckInStatus'
import { useCheckOut } from '../features/checkin/useCheckOut'
import { FamilyStrip } from '../features/family/FamilyStrip'
import { useMyChildren } from '../features/family/useMyChildren'
import { useGymBranding } from '../features/gym/useGymBranding'
import { useGymDisciplines } from '../features/gym/useGymDisciplines'
import { useMyProfile } from '../features/profile/useMyProfile'
import { memberDisplayName } from '../features/profile/displayName'
import { useGymOccupancy } from '../features/gym/useGymOccupancy'
import { GymBar } from '../features/gym/GymBar'
import { useGymRoutes, type GymRoute } from '../features/routes/useGymRoutes'
import { RouteDetailSheet } from '../features/routes/RouteDetailSheet'
import { disciplineForSection, type Discipline } from '@structural-climbing/validation'
import { ChipTabs, type ChipTabOption } from '../design/ChipTabs'
import { FilterMenu } from '../design/FilterMenu'
import { StandingsSheet } from '../features/standings/StandingsSheet'
import { TabTitle } from '../features/news/NewsCard'
import { useHomeNews } from '../features/home/useHomeNews'
import { HomeNewsPanel } from '../features/home/HomeNewsPanel'
import { TodayTops } from '../features/session/TodayTops'
import { PlanLine } from '../features/membership/PlanLine'
import { RoutesTeaser } from '../features/routes/RoutesTeaser'
import { CompetitionTeaser } from '../features/competitions/CompetitionTeaser'
import { useMyClasses } from '../features/classes/useMyClasses'
import { NextClassCard, gymWallClock, nextBooking } from '../features/classes/NextClassCard'
import { SessionStats } from '../features/session/SessionStats'
import { pointsToday } from '../features/session/sessionPoints'
import { useGymGradeConfig } from '../features/gym/useGymGradeConfig'
import { LogTopFab } from '../features/session/LogTopFab'
import { FirstTopCard } from '../features/session/FirstTopCard'
import { StarterRoutes } from '../features/session/StarterRoutes'
import { RecapCard } from '../features/session/RecapCard'
import { QuickTopSheet } from '../features/session/QuickTopSheet'
import { MY_SESSION_CACHE_PREFIX, useMySession } from '../features/session/useMySession'
import { useBoardProgress } from '../features/session/useBoardProgress'
import { disciplinePath } from '../features/nav/navGroups'
import { InstallAppButton } from '../features/pwa/InstallAppButton'
import { PaymentReturnBanner } from '../features/payments/PaymentReturnBanner'
import { usePaidReturn } from '../features/payments/usePaidReturn'
import { useMyPayablePlans } from '../features/payments/useMyPayablePlans'
import { Page } from '../design/Page'
import { fadeRise } from '../design/motion'
import type { GymContext } from '../layouts/GymLayout'

type HomeTab = 'session' | 'family' | 'news'

export function DashboardPage() {
  const { gymId, gymSlug } = useOutletContext<GymContext>()
  const { user, getAccessTokenSilently } = useAuth()
  const { t, i18n } = useTranslation()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const userId = user?.sub as string | undefined

  const { subscription, addons, upcoming, refresh: refreshSubscription } = useMySubscription(gymId)
  const { profile } = useMyProfile()

  const gymBranding = useGymBranding(gymId, apiUrl)
  const occupancy = useGymOccupancy(gymId, apiUrl)
  const disciplines = useGymDisciplines()
  const discipline = disciplines[0]
  const {
    routes,
    total: routesTotal,
    loading: routesLoading,
    setRoutes,
  } = useGymRoutes(gymId, apiUrl, getAccessTokenSilently, 'active', discipline)
  const childList = useMyChildren(gymId)
  // Shares the request the nav makes to decide whether to offer Cursuri.
  const myClasses = useMyClasses(gymId)
  const {
    isCheckedIn,
    checkedInAt,
    childCheckIns,
    refresh: refreshCheckIn,
  } = useMemberCheckInStatus(userId, gymId, apiUrl, getAccessTokenSilently)

  // Follow the member's saved language once the shared profile resolves.
  useEffect(() => {
    if (profile?.language) void i18n.changeLanguage(profile.language)
  }, [profile?.language, i18n])

  // Today at this gym: tops, the visit, the one before it.
  const session = useMySession(gymId)
  // The desk checking you in or out changes all three. Skip the first render:
  // the hook has just loaded them.
  const seenCheckIn = useRef(isCheckedIn)
  useEffect(() => {
    if (seenCheckIn.current === isCheckedIn) return
    seenCheckIn.current = isCheckedIn
    invalidateCachedFetch(MY_SESSION_CACHE_PREFIX)
  }, [isCheckedIn])

  const finishedToday = !isCheckedIn && Boolean(session?.visit?.checkedOutAt)
  // Bouldering and lead are ranked on separate boards. Each wall the gym has
  // gets its own progress (rank, points this visit), asked for only when Home
  // shows a rank: inside the gym, and in the recap.
  const showsRank = isCheckedIn || finishedToday
  const visitStart = session?.visit?.checkedInAt ?? null
  const firstBoard = useBoardProgress({
    gymId,
    discipline: disciplines[0] ?? null,
    enabled: showsRank,
    isCheckedIn,
    visitStart,
  })
  const secondBoard = useBoardProgress({
    gymId,
    discipline: disciplines[1] ?? null,
    enabled: showsRank,
    isCheckedIn,
    visitStart,
  })
  const boardFor = (d: Discipline) => (d === disciplines[1] ? secondBoard : firstBoard)
  // The rank card and the recap follow what you climbed last today.
  const lastTopDiscipline = disciplineForSection(session?.tops[0]?.discipline)
  const rankDiscipline =
    session?.tops.length && disciplines.includes(lastTopDiscipline) ? lastTopDiscipline : discipline
  const gradeConfig = useGymGradeConfig()
  // One session card per wall in a gym with both, each with that wall's tops.
  const topsFor = (d: Discipline) =>
    session
      ? disciplines.length > 1
        ? session.tops.filter((top) => disciplineForSection(top.discipline) === d)
        : session.tops
      : null
  const [logDiscipline, setLogDiscipline] = useState<Discipline>(discipline)
  // The wall the session stats show. Until a chip is tapped, the one you last
  // topped on today.
  const [sessionDiscipline, setSessionDiscipline] = useState<Discipline | null>(null)
  const shownDiscipline =
    sessionDiscipline && disciplines.includes(sessionDiscipline)
      ? sessionDiscipline
      : rankDiscipline

  const [logging, setLogging] = useState(false)
  // The route picked from "Log TOP", open in the wall's own route sheet.
  const [picked, setPicked] = useState<GymRoute | null>(null)
  const logTop = () => {
    setLogDiscipline(shownDiscipline)
    setLogging(true)
  }
  // The leaderboard opens over Home, for the wall on screen, and closes back to
  // it: the same sheet the wall uses, without leaving the session.
  const [standingsOpen, setStandingsOpen] = useState(false)
  // Never topped on this wall, and nothing logged on it today either (the
  // board can lag a moment behind the first log).
  const firstTop =
    boardFor(shownDiscipline).noTopsYet && (topsFor(shownDiscipline)?.length ?? 0) === 0
  const patchRoute = useCallback(
    (routeId: string, patch: Partial<GymRoute>) => {
      setRoutes((prev) => prev.map((r) => (r.id === routeId ? { ...r, ...patch } : r)))
      setPicked((prev) => (prev?.id === routeId ? { ...prev, ...patch } : prev))
    },
    [setRoutes],
  )

  // Home's three parts while you are inside. Family only for a parent;
  // Noutăți only when something is on, with a dot while it is.
  const homeNews = useHomeNews(gymId)
  const homeTabs: ChipTabOption<HomeTab>[] = [
    { key: 'session', label: t('home.tabSession') },
    ...(childList.length > 0
      ? [{ key: 'family' as const, label: `${t('home.tabFamily')} · ${childList.length}` }]
      : []),
    ...(homeNews.hasAny
      ? [
          {
            key: 'news' as const,
            label: <TabTitle label={t('home.tabNews')} dot={homeNews.hasNew} />,
          },
        ]
      : []),
  ]
  const [homeTabChoice, setHomeTab] = useState<HomeTab>('session')
  // A tab whose content went away (the last child removed, the event over)
  // falls back to the session rather than showing an empty page.
  const homeTab = homeTabs.some((o) => o.key === homeTabChoice) ? homeTabChoice : 'session'

  const { checkOut: handleCheckOut, checkOutChild: handleChildCheckOut } = useCheckOut(
    gymId,
    refreshCheckIn,
  )

  // A plan starting later still shows the gym's extras; only the QR waits for it.
  const planLanded = holdsPlan(subscription?.effective_status)
  const isActive = planLanded || isCheckedIn
  // The wall's rule: a plan that lets you in today, or being inside on an entry.
  const canLogAscent = subscription?.effective_status === 'active' || isCheckedIn

  // Back from Stripe. Only the plan counts as arrived, never the check-in: a
  // member can be standing inside the gym on an entry they used this morning.
  const { justPaid, dismiss: dismissPaid } = usePaidReturn({
    hasPlan: planLanded,
    refresh: refreshSubscription,
  })

  // Only ask what the gym sells when the answer could change the screen: the
  // member's plan is over and they are not inside on it. Everybody else, which
  // is most people most of the time, costs nothing.
  const planIsOver = subscription !== undefined && subscription !== null && !planLanded
  const { plans: payablePlans, refresh: refreshPayablePlans } = useMyPayablePlans(
    gymId,
    planIsOver && !isCheckedIn,
  )

  // A refused sale means what we are showing is stale: the gym turned payments
  // off, or the desk sold them a plan while they were deciding.
  const handlePaymentRefused = useCallback(() => {
    void refreshSubscription()
    void refreshPayablePlans()
  }, [refreshSubscription, refreshPayablePlans])

  // One overlay for every code on the account, so a parent shows the whole
  // family's codes without leaving it. Null when closed.
  // `?code=me` opens straight on your code: Contul meu › Abonamentul meu lands
  // here, on the sheet that shows the plan, its validity and the QR together.
  const [searchParams, setSearchParams] = useSearchParams()
  const [codeFor, setCodeForState] = useState<string | null>(() =>
    searchParams.get('code') === 'me' ? 'me' : null,
  )
  const setCodeFor = (key: string | null) => {
    setCodeForState(key)
    if (key === null && searchParams.has('code')) {
      const next = new URLSearchParams(searchParams)
      next.delete('code')
      setSearchParams(next, { replace: true })
    }
  }
  const codePeople = useMemo<CodePerson[]>(() => {
    if (!userId) return []
    const me: CodePerson = {
      key: 'me',
      label: t('classes.me'),
      name: memberDisplayName(profile, user, t('dashboard.greetingFallback')),
      detail: subscription
        ? [
            subscription.gym_plans.name,
            subscription.expires_at
              ? t('membership.validUntil', {
                  date: new Date(subscription.expires_at).toLocaleDateString(),
                })
              : subscription.entries_remaining !== null
                ? t('membership.entriesRemaining', { count: subscription.entries_remaining })
                : null,
          ]
            .filter(Boolean)
            .join(' · ')
        : undefined,
      status:
        subscription?.effective_status === 'active' ? t('membership.statusActive') : undefined,
      payload: memberCodePayload(userId, gymId),
    }
    const kids = childList
      .filter((c) => c.active_subscription?.effective_status === 'active')
      .map((c) => {
        const sub = c.active_subscription!
        return {
          key: c.id,
          label: c.first_name,
          name: [c.first_name, c.last_name].filter(Boolean).join(' '),
          detail: [
            sub.gym_plans.name,
            sub.entries_remaining !== null
              ? t('family.entriesRemaining', { count: sub.entries_remaining })
              : null,
          ]
            .filter(Boolean)
            .join(' · '),
          status: t('membership.statusActive'),
          payload: childCodePayload(userId, gymId, c.id),
        }
      })
    return [me, ...kids]
  }, [userId, gymId, childList, profile, user, subscription, t])

  const booking = myClasses.eligible
    ? nextBooking(myClasses, gymWallClock(new Date(), gymBranding?.timezone ?? null))
    : null
  const classCard = booking && <NextClassCard booking={booking} gymSlug={gymSlug} />
  // Set after you were last here. Empty for a first visit, or when nothing is
  // new, and then the row falls back to the newest routes.
  const lastVisitAt = session?.lastVisitAt ?? null
  const sinceLastVisit = lastVisitAt
    ? routes.filter((r) => new Date(r.created_at).getTime() > new Date(lastVisitAt).getTime())
    : []

  const familyStrip = (
    <FamilyStrip
      childList={childList}
      childCheckIns={childCheckIns}
      gymSlug={gymSlug}
      gymTimezone={gymBranding?.timezone ?? null}
      onShowCode={setCodeFor}
      onCheckOut={handleChildCheckOut}
    />
  )

  const hero = (
    <CheckInHero
      subscription={subscription}
      upcoming={upcoming}
      gymId={gymId}
      isCheckedIn={isCheckedIn}
      checkedInAt={checkedInAt}
      gymTimezone={gymBranding?.timezone ?? null}
      isLastEntry={subscription?.entries_remaining === 1}
      onCheckOut={handleCheckOut}
      payablePlans={payablePlans}
      onPaymentRefused={handlePaymentRefused}
      awaitingPlan={justPaid && !planLanded}
      addons={addons}
      onShowCode={() => setCodeFor('me')}
    />
  )

  const competition = (
    <CompetitionTeaser
      gymId={gymId}
      gymSlug={gymSlug}
      apiUrl={apiUrl}
      getAccessTokenSilently={getAccessTokenSilently}
    />
  )

  return (
    <Page
      // On a phone the gym bar sits in the sticky header; the sidebar layout has
      // no header, so it goes at the top of the page there.
      bleed={
        <div className="hidden md:block">
          <GymBar branding={gymBranding} occupancy={occupancy} gymSlug={gymSlug} />
        </div>
      }
      density="compact"
    >
      {justPaid && (
        <motion.div variants={fadeRise}>
          <PaymentReturnBanner hasPlan={planLanded} onDismiss={dismissPaid} />
        </motion.div>
      )}

      {isCheckedIn ? (
        // ---------------------------------------------------- in the gym
        // Home turns into the session the moment the desk scans you: the
        // check-in arrives live, so there is nothing to refresh.
        <>
          <motion.div variants={fadeRise}>{hero}</motion.div>
          {booking && booking.startsIn <= 30 && classCard}

          {/* Sesiune · Familie · Noutăți. Each part gets the page to itself
              rather than all of them queueing down it. A chip exists only when
              there is something behind it, and with only the session there is
              no row at all. */}
          {homeTabs.length > 1 && (
            <ChipTabs
              aria-label={t('home.tabsLabel')}
              value={homeTab}
              onChange={setHomeTab}
              options={homeTabs}
            />
          )}

          {homeTab === 'family' ? (
            <div role="tabpanel" aria-label={t('home.tabFamily')}>
              <FamilyStrip
                layout="list"
                childList={childList}
                childCheckIns={childCheckIns}
                gymSlug={gymSlug}
                gymTimezone={gymBranding?.timezone ?? null}
                onShowCode={setCodeFor}
                onCheckOut={handleChildCheckOut}
              />
            </div>
          ) : homeTab === 'news' ? (
            <div role="tabpanel" aria-label={t('home.tabNews')}>
              <HomeNewsPanel news={homeNews} gymSlug={gymSlug} />
            </div>
          ) : (
            <motion.div
              variants={fadeRise}
              role="tabpanel"
              aria-label={t('home.tabSession')}
              className="flex flex-col gap-3"
            >
              {/* Two walls: one dropdown, so it never reads as a second row of tabs. */}
              {disciplines.length > 1 && (
                <FilterMenu
                  aria-label={t('routes.discipline')}
                  value={shownDiscipline}
                  onChange={setSessionDiscipline}
                  options={disciplines.map((d) => ({
                    key: d,
                    label:
                      d === 'lead' ? t('routes.disciplineLead') : t('routes.disciplineBouldering'),
                  }))}
                />
              )}
              {firstTop ? (
                <>
                  <FirstTopCard onLogTop={logTop} onOpenStandings={() => setStandingsOpen(true)} />
                  {/* Home reads one wall's routes; for the other, the card alone. */}
                  {shownDiscipline === discipline && (
                    <StarterRoutes
                      routes={routes}
                      apiUrl={apiUrl}
                      wallTo={`/${gymSlug}/${disciplinePath(discipline)}/routes`}
                      onPick={setPicked}
                    />
                  )}
                </>
              ) : (
                <>
                  <SessionStats
                    tops={topsFor(shownDiscipline)}
                    pointsGained={pointsToday(topsFor(shownDiscipline), gradeConfig)}
                    rank={boardFor(shownDiscipline).myEntry?.rank ?? null}
                    onOpenStandings={() => setStandingsOpen(true)}
                  />
                  <TodayTops tops={topsFor(shownDiscipline)} />
                  {booking && booking.startsIn > 30 && classCard}
                  <RoutesTeaser
                    routes={(sinceLastVisit.length > 0 ? sinceLastVisit : routes).filter(
                      (r) => !r.has_ascent,
                    )}
                    total={routesTotal}
                    loading={routesLoading}
                    gymSlug={gymSlug}
                    apiUrl={apiUrl}
                    title={
                      sinceLastVisit.length > 0
                        ? t('session.sinceLastVisit')
                        : t('session.notTried')
                    }
                    emptyLabel={t('session.allTried')}
                    size="sm"
                    onPick={setPicked}
                  />
                </>
              )}
              {firstTop && booking && booking.startsIn > 30 && classCard}
              <PlanLine subscription={subscription} addons={addons} />
            </motion.div>
          )}
          {/* The first-top card has its own button; afterwards it floats. */}
          {!firstTop && (
            <>
              {/* Room for the floating button, so it never covers the last card. */}
              <div aria-hidden className="h-14" />
              <LogTopFab onPress={logTop} />
            </>
          )}
          <StandingsSheet
            isOpen={standingsOpen}
            onClose={() => setStandingsOpen(false)}
            gymId={gymId}
            gymSlug={gymSlug}
            discipline={shownDiscipline}
          />
          <QuickTopSheet
            // Opens on the wall whose card was tapped.
            key={logDiscipline}
            initialDiscipline={logDiscipline}
            isOpen={logging}
            onClose={() => setLogging(false)}
            gymId={gymId}
            gymSlug={gymSlug}
            disciplines={disciplines}
            onPick={(route) => {
              setLogging(false)
              setPicked(route)
            }}
          />
        </>
      ) : (
        // ------------------------------------------------ before you arrive
        // The code is the first thing on screen: that is what Home is opened
        // for at the desk. Everything below it is short.
        <>
          {finishedToday && session && (
            <motion.div variants={fadeRise}>
              <RecapCard
                session={session}
                gymTimezone={gymBranding?.timezone ?? null}
                walls={disciplines
                  .filter((d) => (topsFor(d)?.length ?? 0) > 0)
                  .map((d) => ({
                    label:
                      d === 'lead' ? t('routes.disciplineLead') : t('routes.disciplineBouldering'),
                    tops: topsFor(d) ?? [],
                    points: pointsToday(topsFor(d), gradeConfig),
                    rankFrom: boardFor(d).start?.rank ?? null,
                    rankTo: boardFor(d).myEntry?.rank ?? null,
                  }))}
              />
            </motion.div>
          )}

          <motion.div variants={fadeRise} className="min-w-0">
            <h1 className="truncate text-2xl font-bold tracking-tight">
              {t('dashboard.greeting', {
                name: memberDisplayName(profile, user, t('dashboard.greetingFallback')),
              })}
            </h1>
            <p className="mt-0.5 text-sm text-foreground/50">{t('dashboard.passHint')}</p>
          </motion.div>

          <motion.div variants={fadeRise}>{hero}</motion.div>
          {familyStrip}
          {classCard}

          {isActive && (
            <>
              <RoutesTeaser
                routes={sinceLastVisit.length > 0 ? sinceLastVisit : routes}
                total={routesTotal}
                loading={routesLoading}
                gymSlug={gymSlug}
                apiUrl={apiUrl}
                title={sinceLastVisit.length > 0 ? t('session.sinceLastVisit') : undefined}
                onPick={setPicked}
              />
              {competition}
            </>
          )}

          {/* Phones only: from md up the sidebar carries its own install button.
              At the bottom, so it never pushes the code off the first screen. */}
          <InstallAppButton variant="banner" className="md:hidden" />
        </>
      )}

      {/* The same sheet the wall opens, for a route tapped anywhere on Home or
          picked from Log TOP: attempts, rating and a note for the setters,
          logged exactly as from the wall. */}
      <RouteDetailSheet
        route={picked}
        gymId={gymId}
        userId={userId ?? ''}
        apiUrl={apiUrl}
        discipline={picked ? disciplineForSection(picked.section?.discipline) : discipline}
        getAccessTokenSilently={getAccessTokenSilently}
        onClose={() => setPicked(null)}
        onLogAscent={(routeId, attempts, rating) =>
          patchRoute(routeId, { has_ascent: true, attempts, my_rating: rating })
        }
        onRemoveAscent={(routeId) => patchRoute(routeId, { has_ascent: false, my_rating: null })}
        onUpdateAscent={(routeId, attempts, rating) =>
          patchRoute(routeId, { attempts, my_rating: rating })
        }
        onRatingRefresh={(routeId, avgRating, ratingCount) =>
          patchRoute(routeId, { avg_rating: avgRating, rating_count: ratingCount })
        }
        canLogAscent={canLogAscent}
        // One route at a time from here: no stepping to the next one.
        hasPrev={false}
        hasNext={false}
        nextLoading={false}
        onPrev={() => {}}
        onNext={() => {}}
      />
      <CodeOverlay
        people={codePeople}
        current={codeFor}
        onSelect={setCodeFor}
        onClose={() => setCodeFor(null)}
      />
    </Page>
  )
}
